import React from 'react';

import { useSortable } from '@dnd-kit/sortable';
import { flexRender } from '@tanstack/react-table';
import { Cell as CellDef } from '@tanstack/react-table';
import { alignments, getCommonPinningStyles, WithSubData } from 'ui-kit';

import { Alignment, isColumnMeta } from './utils';

interface RenderCellProps<T> {
  cell: CellDef<WithSubData<T>, unknown>;
  columnOrder: string[];
  width: number;
  isColumnResize: string;
  bgColor?: string;
  extraClasses?: string;
  hoverRowColor?: 'gray' | 'none' | 'brand_dark';
  hoverLeftOffset: number;
  compact?: boolean;
}

const ZIndex_VALUE = 0;
const RenderCellComponent = <T,>({
  hoverLeftOffset,
  cell,
  columnOrder,
  width,
  isColumnResize,
  bgColor,
  extraClasses = '',
  hoverRowColor = 'none',
  compact = false,
}: RenderCellProps<T>) => {
  const { column } = cell;
  const columnDefMeta = column.columnDef.meta;

  const { isDragging, setNodeRef, transform, transition } = useSortable({
    id: column.id,
  });
  const { alignment, numeric } = (() => {
    if (isColumnMeta(columnDefMeta)) {
      return {
        alignment: columnDefMeta.alignment || Alignment.left,
        numeric: columnDefMeta.numeric || false,
      };
    }
    return { alignment: Alignment.left, numeric: false };
  })();

  const hoverColors = {
    brand_dark: 'group-hover:bg-brand-950',
    gray: 'group-hover:bg-gray-1000',
    none: '',
  };

  const isActionColumn = column.id === 'actions';
  const actionClasses = `${isActionColumn ? 'py-[8px] ' : compact ? '' : 'py-[18px]'} pl-2 pr-[10px]`;

  const tdClasses = `${actionClasses}  ${isColumnResize === column.id ? 'border-r-2 border-r-red-400' : ''} ${isDragging ? 'bg-gray-600' : bgColor} ${extraClasses} ${hoverColors[hoverRowColor]}`;

  return (
    <td
      className={tdClasses}
      style={{
        ...(transform && {
          transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
          transition,
        }),
        ...getCommonPinningStyles(column, ZIndex_VALUE, false, isDragging),
      }}
    >
      <div
        ref={setNodeRef}
        style={{
          minWidth: width || cell.column.getSize(),
          width: 'fit-content',
        }}
        data-index={columnOrder.indexOf(column.id)}
        className={`just flex items-center whitespace-nowrap font-pt-mono text-[14px] leading-tight text-white justify-${alignments[alignment]}`}
      >
        {flexRender(column.columnDef.cell, {
          ...cell.getContext(),
          hoverLeftOffset: hoverLeftOffset,
        })}
      </div>
    </td>
  );
};

export const RenderCell = React.memo(RenderCellComponent) as typeof RenderCellComponent;
